import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  MediaAsset,
  MediaUploadInitRequest,
  MediaUploadInitResponse,
  MediaKind,
} from '@pirouette/shared';
import { mediaUploadInitRequestSchema, probeVideoDuration } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';

// ---- Listings -------------------------------------------------------------

export function useMediaForDance(danceId: string | undefined) {
  return useQuery({
    enabled: !!danceId,
    queryKey: ['media', 'dance', danceId],
    queryFn: async (): Promise<MediaAsset[]> => {
      const { data, error } = await supabase
        .from('media_assets')
        .select('*')
        .eq('dance_id', danceId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useMediaForRoutine(routineId: string | undefined) {
  return useQuery({
    enabled: !!routineId,
    queryKey: ['media', 'routine', routineId],
    queryFn: async (): Promise<MediaAsset[]> => {
      const { data, error } = await supabase
        .from('media_assets')
        .select('*')
        .eq('competition_routine_id', routineId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

/**
 * All media the caller's RLS lets them see. For a parent this is exactly
 * the videos for dances/routines their dancer is cast in (Sprint 6 RLS
 * policies). For admin, everything.
 */
export function useMyVisibleMedia() {
  return useQuery({
    queryKey: ['media', 'visible'],
    queryFn: async (): Promise<MediaAsset[]> => {
      const { data, error } = await supabase
        .from('media_assets')
        .select('*')
        .eq('upload_status', 'complete')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

// ---- Signed-URL playback --------------------------------------------------
// Calls the Sprint-6 request_media_signed_url RPC under the caller's JWT;
// the RPC enforces RLS + logs an access entry. The Edge Function returns
// the presigned R2 GET URL (1 h TTL per plan §6.5).

export function useMediaSignedUrl(assetId: string | undefined) {
  return useQuery({
    enabled: !!assetId,
    // Fresh URL on each focus to avoid TTL expiry surprising the player.
    refetchOnWindowFocus: true,
    staleTime: 30 * 60 * 1000, // 30 min — well under the 1 h R2 TTL
    queryKey: ['media-signed-url', assetId],
    queryFn: async (): Promise<{ url: string; expires_in: number }> => {
      const res = await supabase.functions.invoke<
        { url: string; expires_in: number } | { error: string }
      >('sign-media-url', {
        body: { asset_id: assetId },
      });
      if (res.error) throw res.error;
      const resp = res.data!;
      if ('error' in resp) throw new Error(resp.error);
      return resp;
    },
  });
}

// ---- Upload orchestration -------------------------------------------------

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

/**
 * Drives the full upload flow:
 *   1. POST /functions/v1/media-upload-init       → asset_id + presigned PUT URL
 *   2. XMLHttpRequest PUT directly to R2          → reports progress
 *   3. POST /functions/v1/media-upload-complete   → HEAD + finalize
 *
 * Uses XMLHttpRequest rather than fetch so we get `upload.onprogress` —
 * fetch's streaming upload progress is not yet broadly available.
 */
export function useUploadMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      file: File;
      init: MediaUploadInitRequest;
      onProgress?: (p: UploadProgress) => void;
    }): Promise<{ asset_id: string }> => {
      const parsed = mediaUploadInitRequestSchema.parse(args.init);

      // 1. Init — reserve the asset + get the presigned PUT URL.
      const initResp = await supabase.functions.invoke('media-upload-init', {
        body: parsed,
      });
      if (initResp.error) throw initResp.error;
      const init = initResp.data as MediaUploadInitResponse | { error: string };
      if ('error' in init) throw new Error(init.error);

      // 2. Direct browser → R2 upload via XHR for progress.
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', init.upload_url, true);
        xhr.setRequestHeader('Content-Type', init.content_type);
        xhr.upload.onprogress = (e) => {
          if (!e.lengthComputable) return;
          args.onProgress?.({
            loaded: e.loaded,
            total: e.total,
            percent: Math.round((e.loaded / e.total) * 100),
          });
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`R2 PUT failed: ${xhr.status} ${xhr.responseText.slice(0, 200)}`));
        };
        xhr.onerror = () => reject(new Error('R2 PUT network error'));
        xhr.send(args.file);
      });

      // 3. Probe duration (videos only) — best effort.
      let durationSeconds: number | undefined;
      if (parsed.kind === 'video') {
        const dur = await probeVideoDuration(args.file);
        if (dur != null) durationSeconds = dur;
      }

      // 4. Complete — HEAD + finalize on the back end.
      const completeResp = await supabase.functions.invoke('media-upload-complete', {
        body: {
          asset_id: init.asset_id,
          duration_seconds: durationSeconds,
        },
      });
      if (completeResp.error) throw completeResp.error;
      const completion = completeResp.data as { ok: boolean } | { error: string };
      if ('error' in completion) throw new Error(completion.error);

      return { asset_id: init.asset_id };
    },
    onSuccess: (_, args) => {
      if (args.init.dance_id) {
        void qc.invalidateQueries({ queryKey: ['media', 'dance', args.init.dance_id] });
      }
      if (args.init.competition_routine_id) {
        void qc.invalidateQueries({
          queryKey: ['media', 'routine', args.init.competition_routine_id],
        });
      }
      void qc.invalidateQueries({ queryKey: ['media', 'visible'] });
    },
  });
}

export function useDeleteMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string }) => {
      // Deletes the DB row. The R2 object becomes orphaned until the
      // Sprint 10 nightly sweeper reaps it. Signed URLs that were already
      // issued (1 h TTL) keep working — that's a property of presigned
      // URLs, not specific to this delete path; the framework explicitly
      // scopes this as "deter casual" (plan §12).
      const { error } = await supabase.from('media_assets').delete().eq('id', args.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['media'] });
    },
  });
}

// ---- Per-dancer convenience view ------------------------------------------

interface DanceRow {
  dance_id: string;
}

/**
 * Resolve every dance + routine the dancer is cast in, then aggregate
 * media for each. RLS limits the dance/routine ids the caller can read,
 * which transitively limits the media.
 */
export function useMediaForDancer(dancerId: string | undefined) {
  return useQuery({
    enabled: !!dancerId,
    queryKey: ['media', 'dancer', dancerId],
    queryFn: async (): Promise<{
      dances: { id: string; name: string; media: MediaAsset[] }[];
      routines: { id: string; name: string; media: MediaAsset[] }[];
    }> => {
      const { data: casts, error: e1 } = await supabase
        .from('cast_assignments')
        .select('dance_id, dances(id, name)')
        .eq('dancer_id', dancerId!);
      if (e1) throw e1;
      const danceIds = [...new Set(((casts ?? []) as DanceRow[]).map((r) => r.dance_id))];

      const { data: routines, error: e2 } = await supabase
        .from('competition_routines')
        .select('id, dance_id, dances(name)')
        .in('dance_id', danceIds.length ? danceIds : ['00000000-0000-0000-0000-000000000000']);
      if (e2) throw e2;
      const routineIds = ((routines ?? []) as { id: string }[]).map((r) => r.id);

      const danceMediaPromise =
        danceIds.length > 0
          ? supabase
              .from('media_assets')
              .select('*')
              .in('dance_id', danceIds)
              .eq('upload_status', 'complete')
          : Promise.resolve({ data: [], error: null });

      const routineMediaPromise =
        routineIds.length > 0
          ? supabase
              .from('media_assets')
              .select('*')
              .in('competition_routine_id', routineIds)
              .eq('upload_status', 'complete')
          : Promise.resolve({ data: [], error: null });

      const [dm, rm] = await Promise.all([danceMediaPromise, routineMediaPromise]);
      if (dm.error) throw dm.error;
      if (rm.error) throw rm.error;

      const danceMedia = (dm.data ?? []) as MediaAsset[];
      const routineMedia = (rm.data ?? []) as MediaAsset[];

      const danceLookup = new Map<string, { id: string; name: string }>();
      for (const c of (casts ?? []) as {
        dance_id: string;
        dances: { id: string; name: string } | null;
      }[]) {
        if (c.dances) danceLookup.set(c.dance_id, c.dances);
      }

      const routineNames = new Map<string, string>();
      for (const r of (routines ?? []) as {
        id: string;
        dances: { name: string } | null;
      }[]) {
        routineNames.set(r.id, r.dances?.name ?? '');
      }

      return {
        dances: [...danceLookup.values()]
          .map((d) => ({
            id: d.id,
            name: d.name,
            media: danceMedia.filter((m) => m.dance_id === d.id),
          }))
          .filter((d) => d.media.length > 0),
        routines: routineIds
          .map((rid) => ({
            id: rid,
            name: routineNames.get(rid) ?? '(routine)',
            media: routineMedia.filter((m) => m.competition_routine_id === rid),
          }))
          .filter((r) => r.media.length > 0),
      };
    },
  });
}

// avoid lint complaints on imports that are only types used for documentation
export type { MediaKind };
